<template>
  <div>
    <transition name="nv-fade">
      <div v-if="open" class="nv-backdrop" @click="close"></div>
    </transition>

    <transition name="nv-slide">
      <aside v-if="open" class="nv-panel" role="dialog" :aria-label="$t('appViews.news.ariaPanel')">
        <!-- Cabecera compacta -->
        <header class="nv-head">
          <span class="nv-head__ic" v-html="ICON_BULB"></span>
          <h3 class="nv-head__t">{{ $t('appViews.news.latestUpdates') }}</h3>
          <button class="nv-x" @click="close" :aria-label="$t('appViews.news.close')">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6 6 18"/></svg>
          </button>
        </header>

        <!-- Pestañas: mitad y mitad, texto a la izquierda -->
        <div v-if="tab !== 'form'" class="nv-tabs">
          <button class="nv-tab" :class="{ on: tab === 'list' }" @click="tab = 'list'">{{ $t('appViews.news.tabNews') }}</button>
          <button class="nv-tab" :class="{ on: tab === 'tickets' }" @click="verReportes">
            {{ $t('appViews.news.reports') }}
            <span v-if="ticketsOpen > 0" class="nv-b">{{ ticketsOpen }}</span>
          </button>
        </div>

        <!-- Novedades, agrupadas por día -->
        <div v-if="tab === 'list'" class="nv-body">
          <div v-if="loading" class="nv-empty">{{ $t('appViews.news.loading') }}</div>
          <div v-else-if="!entries.length" class="nv-empty">
            <span v-html="ICON_BULB"></span>
            <p>{{ $t('appViews.news.emptyNews') }}</p>
          </div>

          <template v-for="(grupo, dia) in porDia">
            <div class="nv-day" :key="'d' + dia">{{ dia }}</div>
            <article v-for="e in grupo" :key="e.id" class="nv-c" :class="{ 'is-open': openId === e.id }">
              <span v-if="e.tag" class="nv-kind" :class="'nv-kind--' + tagCls(e.tag)">{{ e.tag }}</span>
              <span v-if="esNueva(e)" class="nv-new-dot">{{ $t('appViews.news.new') }}</span>

              <h4 class="nv-c__t">{{ e.title }}</h4>
              <p v-if="e.body" class="nv-c__b" :class="{ 'is-clamp': openId !== e.id }" :data-id="e.id">{{ e.body }}</p>

              <div v-if="openId === e.id && e.images && e.images.length" class="nv-mos" :class="'nv-mos--' + mosaico(e.images.length)">
                <button
                  v-for="(img, i) in e.images.slice(0, 5)" :key="img.id" type="button"
                  class="nv-tile" @click.stop="abrirVisor(e.images, i)"
                >
                  <img :src="img.url" :alt="e.title" loading="lazy" />
                  <span class="nv-tile__lupa">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5M11 8v6M8 11h6"/></svg>
                  </span>
                  <span v-if="i === 4 && e.images.length > 5" class="nv-tile__mas">+{{ e.images.length - 5 }}</span>
                </button>
              </div>

              <div class="nv-c__f">
                <button v-if="tieneMas(e)" class="nv-pill nv-pill--more" @click="toggle(e.id)">
                  <span>{{ openId === e.id ? $t('appViews.news.showLess') : $t('appViews.news.showMore') }}</span>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>
                </button>
                <span v-else></span>
                <button class="nv-pill nv-pill--rep" @click.stop="reportarDesde(e)">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z"/></svg>
                  <span>{{ $t('appViews.news.reportProblem') }}</span>
                </button>
              </div>
            </article>
          </template>
        </div>

        <!-- Mis reportes -->
        <div v-if="tab === 'tickets'" class="nv-body">
          <div v-if="loadingTk" class="nv-empty">{{ $t('appViews.news.loading') }}</div>
          <div v-else-if="!tickets.length" class="nv-empty">
            <span v-html="ICON_BULB"></span>
            <p>{{ $t('appViews.news.emptyReports') }}</p>
            <button class="nv-new" @click="reportarDesde(null)">{{ $t('appViews.news.sendReport') }}</button>
          </div>
          <template v-else>
            <article v-for="t in tickets" :key="t.id" class="nv-t" :class="'nv-t--' + stCls(t.status)" @click="abrirChat(t)">
              <div class="nv-t__hd">
                <span class="nv-t__st">{{ stLabel(t.status) }}</span>
                <span v-if="sinLeer(t)" class="nv-t__unread">{{ sinLeer(t) }}</span>
                <span class="nv-t__id">#{{ t.id }}</span>
              </div>
              <h4 class="nv-t__t">{{ t.title }}</h4>
              <p v-if="t.description" class="nv-t__d">{{ t.description }}</p>
              <div class="nv-t__f">
                <span>{{ fmtDate(t.created_at) }}</span>
                <span v-if="t.images && t.images.length">{{ $tc('appViews.news.capturesCount', t.images.length, { count: t.images.length }) }}</span>
                <span class="nv-t__go">{{ $t('appViews.news.viewConversation') }}</span>
              </div>
            </article>
            <button class="nv-new" @click="reportarDesde(null)">{{ $t('appViews.news.sendNewReport') }}</button>
          </template>
        </div>

        <!-- Conversación de un reporte -->
        <div v-if="tab === 'chat'" class="nv-chat">
          <div class="nv-chat__hd">
            <button class="nv-back" @click="tab = 'tickets'">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg> {{ $t('appViews.news.reports') }}
            </button>
            <span class="nv-t__st" :class="'nv-st--' + stCls(chatTicket ? chatTicket.status : 'open')">{{ stLabel(chatTicket ? chatTicket.status : 'open') }}</span>
          </div>

          <div class="nv-chat__cab">
            <h4 class="nv-chat__t">{{ chatTicket ? chatTicket.title : '' }}</h4>
            <span class="nv-chat__sub">#{{ chatTicket ? chatTicket.id : '' }} · {{ $t('appViews.news.openedOn', { date: chatTicket ? fmtDate(chatTicket.created_at) : '' }) }}</span>
          </div>

          <div ref="hilo" class="nv-hilo">
            <span class="nv-hilo__ini">{{ $t('appViews.news.conversationWithSupport') }}</span>

            <transition-group name="nv-burbuja" tag="div" class="nv-hilo__in">
              <div
                v-for="m in hiloMensajes" :key="m.key"
                class="nv-fila" :class="[m.author_type === 'host' ? 'nv-fila--yo' : 'nv-fila--eq', { 'is-seguido': m.seguido }]"
              >
                <span v-if="m.author_type !== 'host'" class="nv-av" :class="{ 'is-hueco': m.seguido }">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3a7 7 0 0 0-7 7v3a2 2 0 0 0 2 2h1v-5H7v-.4A5 5 0 0 1 17 10v5h-3v2h3a2 2 0 0 0 2-2v-5a7 7 0 0 0-7-7Z"/></svg>
                </span>
                <div class="nv-msg" :class="m.author_type === 'host' ? 'nv-msg--host' : 'nv-msg--agent'">
                  <p>{{ m.body }}</p>
                  <span v-if="m.ultimo" class="nv-msg__m">{{ m.author_type === 'host' ? $t('appViews.news.you') : (m.author_name || $t('appViews.news.support')) }} · {{ m.inicial ? fmtDate(m.created_at) : fmtHora(m.created_at) }}</span>
                </div>
              </div>
            </transition-group>

            <transition name="nv-burbuja">
              <div v-if="escribiendo" class="nv-fila nv-fila--eq">
                <span class="nv-av">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3a7 7 0 0 0-7 7v3a2 2 0 0 0 2 2h1v-5H7v-.4A5 5 0 0 1 17 10v5h-3v2h3a2 2 0 0 0 2-2v-5a7 7 0 0 0-7-7Z"/></svg>
                </span>
                <div class="nv-msg nv-msg--agent nv-msg--typing">
                  <span class="nv-dot"></span><span class="nv-dot"></span><span class="nv-dot"></span>
                </div>
              </div>
            </transition>

            <p v-if="!mensajes.length && !escribiendo" class="nv-hilo__espera">
              {{ $t('appViews.news.noReplyYet') }}
            </p>
          </div>

          <div class="nv-rp" :class="{ 'is-foco': focoResp }">
            <textarea
              ref="caja"
              v-model="respuesta"
              class="nv-rp__ta"
              rows="1"
              :placeholder="$t('appViews.news.replyPlaceholder')"
              @focus="focoResp = true"
              @blur="focoResp = false"
              @input="alEscribirYo"
              @keydown.enter.exact.prevent="responder"
            ></textarea>
            <button class="nv-rp__go" :disabled="enviandoResp || !respuesta.trim()" @click="responder" :aria-label="$t('appViews.news.send')">
              <svg v-if="!enviandoResp" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4.5 12 20 4.5 15 20l-3.6-5.4L4.5 12Z"/><path d="m11.4 14.6 3.4-3.4"/></svg>
              <span v-else class="nv-spin"></span>
            </button>
          </div>
          <span class="nv-rp__pista" v-html="$t('appViews.news.replyHint')"></span>
        </div>

        <!-- Formulario -->
        <div v-if="tab === 'form'" class="nv-form">
          <button class="nv-back" @click="tab = 'list'">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg> {{ $t('appViews.news.back') }}
          </button>
          <h4 class="nv-form__t">{{ $t('appViews.news.sendReportTitle') }}</h4>
          <p class="nv-form__s">{{ $t('appViews.news.formSubtitle') }}</p>

          <div v-if="form.contexto" class="nv-ctx"><span class="nv-ctx__l">{{ $t('appViews.news.about') }}</span> {{ form.contexto }}</div>
          <div v-if="formError" class="nv-err">{{ formError }}</div>

          <label class="nv-l">{{ $t('appViews.news.whatHappens') }}</label>
          <input v-model="form.title" class="nv-i" :placeholder="$t('appViews.news.titlePlaceholder')" />

          <label class="nv-l">{{ $t('appViews.news.describeDetail') }}</label>
          <textarea v-model="form.description" class="nv-i nv-i--a" :placeholder="$t('appViews.news.descPlaceholder')"></textarea>

          <label class="nv-l">{{ $t('appViews.news.capture') }} <span class="nv-opt">{{ $t('appViews.news.optional') }}</span></label>
          <div v-if="form.images.length" class="nv-shots">
            <div v-for="(img, i) in form.images" :key="i" class="nv-shot">
              <img :src="img" alt="" />
              <button class="nv-shot__x" @click="form.images.splice(i, 1)">×</button>
            </div>
          </div>
          <label class="nv-drop">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M12 15V3m0 0-4 4m4-4 4 4"/><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/></svg>
            <span>{{ $t('appViews.news.attachHint') }}</span>
            <input type="file" accept="image/*" multiple hidden @change="onShots" />
          </label>

          <button class="nv-send" :disabled="submitting" @click="enviar">{{ submitting ? $t('appViews.news.sending') : $t('appViews.news.submitReport') }}</button>
        </div>
      </aside>
    </transition>

    <transition name="nv-fade">
      <div v-if="visor" class="nv-visor" @click="cerrarVisor">
        <button class="nv-visor__x" @click.stop="cerrarVisor" :aria-label="$t('appViews.news.close')">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6 6 18"/></svg>
        </button>

        <button v-if="visor.imgs.length > 1" class="nv-visor__nav nv-visor__nav--iz" @click.stop="visorAnt" :aria-label="$t('appViews.news.prev')">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg>
        </button>

        <div class="nv-visor__centro" @click.stop>
          <transition :name="'nv-sw-' + visorDir" mode="out-in">
            <figure :key="visor.i" class="nv-visor__marco">
              <img :src="visor.imgs[visor.i].url" alt="" />
            </figure>
          </transition>
          <div v-if="visor.imgs.length > 1" class="nv-visor__tira">
            <button v-for="(im, i) in visor.imgs" :key="im.id" class="nv-mini" :class="{ on: i === visor.i }" @click="irA(i)">
              <img :src="im.url" alt="" />
            </button>
          </div>
        </div>

        <button v-if="visor.imgs.length > 1" class="nv-visor__nav nv-visor__nav--de" @click.stop="visorSig" :aria-label="$t('appViews.news.next')">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>
        </button>

        <span v-if="visor.imgs.length > 1" class="nv-visor__c">{{ visor.i + 1 }} / {{ visor.imgs.length }}</span>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from "axios";

const ICON_BULB =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M9.5 17h5"/><path d="M10.2 20h3.6"/><path d="M12 4.2a5 5 0 0 0-3 9c.5.4.8.9.9 1.5l.1.5h4l.1-.5c.1-.6.4-1.1.9-1.5a5 5 0 0 0-3-9Z"/><path d="M12 1.4v1M4.6 4.6l.7.7M19.4 4.6l-.7.7M2.2 11.6h1M20.8 11.6h1"/></svg>';

const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

export default {
  name: "NovedadesDrawer",
  data() {
    return {
      ICON_BULB,
      open: false,
      tab: "list",
      loading: false,
      loadingTk: false,
      submitting: false,
      entries: [],
      tickets: [],
      unread: 0,
      ticketsOpen: 0,
      openId: null,
      visor: null,
      visorDir: "sig",
      desborda: {},
      temporizadorMedir: null,
      focoResp: false,
      cargadoUnaVez: false,
      vistoHasta: null,
      formError: "",
      form: { title: "", description: "", images: [], contexto: "", changelog_entry_id: null },
      chatTicket: null,
      mensajes: [],
      respuesta: "",
      enviandoResp: false,
      escribiendo: false,
      temporizadorEscribe: null,
      ultimoAviso: 0,
      reintentoRt: null,
    };
  },
  computed: {
    /** Agrupadas por día de publicación, respetando el orden que llega. */
    porDia() {
      const out = {};
      for (const e of this.entries) {
        const k = this.fmtDate(e.published_at) || "Sin fecha";
        if (!out[k]) out[k] = [];
        out[k].push(e);
      }
      return out;
    },
    /** El hilo real: la descripción inicial cuenta como el primer mensaje.
     *  Marcamos si va seguido del mismo autor para juntar las burbujas y
     *  enseñar la firma solo en la última de cada tanda. */
    hiloMensajes() {
      const t = this.chatTicket;
      const bruto = [];
      if (t && t.description) {
        bruto.push({ key: "ini", author_type: "host", body: t.description, created_at: t.created_at, inicial: true });
      }
      for (const m of this.mensajes) bruto.push({ ...m, key: "m" + m.id });
      return bruto.map((m, i) => ({
        ...m,
        seguido: i > 0 && bruto[i - 1].author_type === m.author_type,
        ultimo: i === bruto.length - 1 || bruto[i + 1].author_type !== m.author_type,
      }));
    },
  },
  watch: {
    entries() { this.medirDesbordes(); },
  },
  mounted() {
    this.$root.$on("open-novedades", this.abrir);
    window.addEventListener("resize", this.alRedimensionar);
    this.cargarContador();
    this.conectarRealtime();
  },
  beforeDestroy() {
    this.$root.$off("open-novedades", this.abrir);
    window.removeEventListener("resize", this.alRedimensionar);
    clearTimeout(this.temporizadorMedir);
    document.removeEventListener("keydown", this.onKey);
    this.desconectarRealtime();
    clearTimeout(this.temporizadorEscribe);
  },
  methods: {
    async cargarContador() {
      try {
        const { data } = await axios.get("/changelog");
        this.entries = data.entries || [];
        this.unread = data.unread_count || 0;
        this.vistoHasta = data.seen_at || null;
        this.cargadoUnaVez = true;
        this.$root.$emit("novedades-unread", this.unread);
      } catch (e) { /* si falla, no aparece el badge */ }
    },
    async abrir() {
      this.open = true;
      this.tab = "list";
      document.addEventListener("keydown", this.onKey);
      if (!this.cargadoUnaVez) {
        this.loading = true;
        try {
          const { data } = await axios.get("/changelog");
          this.entries = data.entries || [];
          this.unread = data.unread_count || 0;
          this.vistoHasta = data.seen_at || null;
        } catch (e) { /* noop */ } finally { this.loading = false; }
      }
      if (this.unread > 0) this.marcarVisto();
      this.medirDesbordes();
      this.cargarReportes();
    },
    async marcarVisto() {
      this.unread = 0;
      this.$root.$emit("novedades-unread", 0);
      try { await axios.post("/changelog/seen"); } catch (e) { /* noop */ }
    },
    async cargarReportes() {
      try {
        const { data } = await axios.get("/support-tickets");
        this.tickets = data.tickets || [];
        this.ticketsOpen = data.open || 0;
      } catch (e) { /* noop */ }
    },
    verReportes() {
      this.tab = "tickets";
      if (!this.tickets.length) this.cargarReportes();
    },
    toggle(id) { this.openId = this.openId === id ? null : id; },

    // ── Novedades: solo ofrecemos "Ver más" si de verdad hay más ────────────
    /** Una novedad de dos líneas y sin capturas no tiene nada que desplegar:
     *  el botón no haría nada y solo estorba. */
    tieneMas(e) {
      if (e.images && e.images.length) return true;
      return !!this.desborda[e.id];
    },
    /** Medimos sobre el DOM real: con el recorte de 2 líneas puesto,
     *  scrollHeight > clientHeight significa que hay texto escondido.
     *  Hay que esperar a que DM Sans esté cargada: con la fuente de reserva
     *  el texto ocupa otro ancho y salían "Ver más" que no llevaban a nada. */
    medirDesbordes() {
      const medir = () => this.$nextTick(() => {
        const marca = {};
        const nodos = this.$el ? this.$el.querySelectorAll(".nv-c__b[data-id]") : [];
        for (const n of nodos) {
          if (!n.classList.contains("is-clamp")) continue;   // abierta: ya se ve entera
          marca[n.dataset.id] = n.scrollHeight - n.clientHeight > 2;
        }
        this.desborda = { ...this.desborda, ...marca };
      });
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(medir).catch(medir);
      else medir();
    },
    alRedimensionar() {
      if (!this.open) return;
      clearTimeout(this.temporizadorMedir);
      this.temporizadorMedir = setTimeout(this.medirDesbordes, 200);
    },
    /** Reparto del mosaico según cuántas capturas haya. */
    mosaico(n) { return n >= 5 ? "5" : String(n); },

    // ── Visor con carrusel ─────────────────────────────────────────────────
    abrirVisor(imgs, i) { this.visorDir = "sig"; this.visor = { imgs, i: i || 0 }; },
    cerrarVisor() { this.visor = null; },
    visorSig() {
      if (!this.visor) return;
      this.visorDir = "sig";
      this.visor.i = (this.visor.i + 1) % this.visor.imgs.length;
    },
    visorAnt() {
      if (!this.visor) return;
      this.visorDir = "ant";
      this.visor.i = (this.visor.i - 1 + this.visor.imgs.length) % this.visor.imgs.length;
    },
    irA(i) {
      if (!this.visor) return;
      this.visorDir = i > this.visor.i ? "sig" : "ant";
      this.visor.i = i;
    },

    // ── Conversación del reporte ────────────────────────────────────────────
    async abrirChat(t) {
      this.chatTicket = t;
      this.mensajes = [];
      this.respuesta = "";
      this.tab = "chat";
      try {
        const { data } = await axios.get(`/support-tickets/${t.id}/messages`);
        this.mensajes = data.messages || [];
        this.alFinal();
      } catch (e) { /* noop */ }
    },
    async responder() {
      const texto = this.respuesta.trim();
      if (!texto || !this.chatTicket || this.enviandoResp) return;
      this.enviandoResp = true;
      try {
        const { data } = await axios.post(`/support-tickets/${this.chatTicket.id}/messages`, { body: texto });
        if (data.message) this.mensajes.push(data.message);
        this.respuesta = "";
        this.$nextTick(this.autoAlto);
        this.alFinal();
        this.cargarReportes();
      } catch (e) { /* noop */ } finally { this.enviandoResp = false; }
    },
    /** Al teclear: la caja crece con el texto (hasta un tope) y avisamos al
     *  agente de que estamos escribiendo. */
    alEscribirYo() {
      this.autoAlto();
      this.avisarEscribiendo();
    },
    autoAlto() {
      const el = this.$refs.caja;
      if (!el) return;
      el.style.height = "auto";
      el.style.height = Math.min(el.scrollHeight, 132) + "px";
    },
    /** Aviso de "escribiendo", como mucho uno cada 2 s. */
    avisarEscribiendo() {
      const ahora = Date.now();
      if (!this.chatTicket || ahora - this.ultimoAviso < 2000) return;
      this.ultimoAviso = ahora;
      axios.post(`/support-tickets/${this.chatTicket.id}/typing`).catch(() => {});
    },
    alFinal() {
      this.$nextTick(() => {
        const el = this.$refs.hilo;
        if (!el) return;
        try { el.scrollTo({ top: el.scrollHeight, behavior: "smooth" }); }
        catch (e) { el.scrollTop = el.scrollHeight; }
      });
    },
    /** Mensajes del equipo que aún no he leído. */
    sinLeer(t) {
      return (t.unread_for_host || 0) || 0;
    },
    fmtHora(iso) {
      if (!iso) return "";
      try {
        const d = new Date(iso);
        return d.toLocaleTimeString("es", { hour: "2-digit", minute: "2-digit" });
      } catch (e) { return ""; }
    },

    // ── Realtime: escuchamos el socket que el PMS ya tiene abierto ──────────
    conectarRealtime(intento) {
      const s = this.socket();
      const owner = this.ownerId();
      // El socket y la sesión pueden no estar listos cuando monta el drawer.
      // Reintentamos un rato en vez de quedarnos mudos para siempre.
      if (!s || !owner) {
        const n = (intento || 0) + 1;
        if (n <= 10) this.reintentoRt = setTimeout(() => this.conectarRealtime(n), 1500);
        return;
      }
      this.evMensaje = `ticket-message-${owner}`;
      this.evEscribe = `ticket-typing-${owner}`;
      s.on(this.evMensaje, this.alLlegarMensaje);
      s.on(this.evEscribe, this.alEscribirOtro);
    },
    desconectarRealtime() {
      clearTimeout(this.reintentoRt);
      const s = this.socket();
      if (!s) return;
      // Importante: pasar el handler. Un off(evento) a secas borraría también
      // los oyentes de otras partes del PMS.
      if (this.evMensaje) s.off(this.evMensaje, this.alLlegarMensaje);
      if (this.evEscribe) s.off(this.evEscribe, this.alEscribirOtro);
    },
    socket() {
      try { return require("@/services/socketio.service").default.socket || null; } catch (e) { return null; }
    },
    ownerId() {
      const u = this.$store && this.$store.state ? this.$store.state.userRoot : null;
      return u ? (u.owner_id || u.id) : null;
    },
    alLlegarMensaje(carga) {
      const d = carga && carga.data ? carga.data : carga;
      if (!d || !d.message) return;
      // Si estoy en esa conversación, lo pinto; si no, refresco el contador.
      if (this.chatTicket && Number(d.ticket_id) === Number(this.chatTicket.id)) {
        const yaEsta = this.mensajes.some((m) => m.id === d.message.id);
        if (!yaEsta) { this.mensajes.push(d.message); this.alFinal(); }
        this.escribiendo = false;
      } else {
        this.cargarReportes();
      }
    },
    alEscribirOtro(carga) {
      const d = carga && carga.data ? carga.data : carga;
      if (!d || !this.chatTicket || Number(d.ticket_id) !== Number(this.chatTicket.id)) return;
      if (d.who === "host") return;   // soy yo
      this.escribiendo = true;
      clearTimeout(this.temporizadorEscribe);
      this.temporizadorEscribe = setTimeout(() => { this.escribiendo = false; }, 3500);
    },
    reportarDesde(entry) {
      this.formError = "";
      this.form = {
        title: "", description: "", images: [],
        contexto: entry ? entry.title : "",
        changelog_entry_id: entry ? entry.id : null,
      };
      this.tab = "form";
    },
    async onShots(ev) {
      const files = Array.from(ev.target.files || []);
      ev.target.value = "";
      for (const f of files.slice(0, 5 - this.form.images.length)) {
        try { this.form.images.push(await this.fileToDataUrl(f)); } catch (e) { /* noop */ }
      }
    },
    async enviar() {
      if (!this.form.title.trim()) { this.formError = this.$t("appViews.news.errRequired"); return; }
      this.submitting = true;
      this.formError = "";
      try {
        await axios.post("/support-tickets", {
          title: this.form.title.trim(),
          description: this.form.description.trim() || null,
          changelog_entry_id: this.form.changelog_entry_id,
          pms_route: this.$route ? this.$route.fullPath : null,
          images: this.form.images,
        });
        await this.cargarReportes();
        this.tab = "tickets";
      } catch (e) {
        this.formError = this.$t("appViews.news.errSend");
      } finally {
        this.submitting = false;
      }
    },
    close() {
      this.open = false;
      this.visor = null;
      document.removeEventListener("keydown", this.onKey);
    },
    onKey(ev) {
      if (this.visor) {
        if (ev.key === "Escape") { this.cerrarVisor(); return; }
        if (ev.key === "ArrowRight") { ev.preventDefault(); this.visorSig(); return; }
        if (ev.key === "ArrowLeft") { ev.preventDefault(); this.visorAnt(); return; }
        return;
      }
      if (ev.key === "Escape") this.close();
    },
    fileToDataUrl(file) {
      return new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(String(r.result)); r.onerror = rej; r.readAsDataURL(file); });
    },
    tagCls(tag) { const t = String(tag || "").toLowerCase(); if (t.startsWith("mejor")) return "mej"; if (t.startsWith("corr")) return "cor"; return "nov"; },
    stCls(s) { return { open: "o", in_progress: "p", resolved: "d", closed: "c" }[s] || "o"; },
    stLabel(s) {
      const map = {
        open: this.$t("appViews.news.status.open"),
        in_progress: this.$t("appViews.news.status.in_progress"),
        resolved: this.$t("appViews.news.status.resolved"),
        closed: this.$t("appViews.news.status.closed"),
      };
      return map[s] || this.$t("appViews.news.status.open");
    },
    fmtDate(iso) {
      if (!iso) return "";
      try {
        const d = new Date(iso);
        const hoy = new Date();
        const dias = Math.round((new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate()) - new Date(d.getFullYear(), d.getMonth(), d.getDate())) / 86400000);
        if (dias === 0) return this.$t("appViews.news.today");
        if (dias === 1) return this.$t("appViews.news.yesterday");
        return d.getDate() + " " + MESES[d.getMonth()] + " " + d.getFullYear();
      } catch (e) { return ""; }
    },
    /** Publicada después de la última vez que el anfitrión miró. */
    esNueva(e) {
      if (!this.vistoHasta || !e.published_at) return !this.vistoHasta;
      try { return new Date(e.published_at) > new Date(this.vistoHasta); } catch (err) { return false; }
    },
  },
};
</script>

<style scoped>
/* DM Sans a la fuerza: el PMS arrastra CSS antiguo (vuesax/bootstrap) que pisa
   la fuente y los títulos salían en Inter. */
.nv-panel, .nv-panel * { font-family: "DM Sans", system-ui, -apple-system, sans-serif !important; box-sizing: border-box; }

.nv-panel {
  position: fixed; top: 0; right: 0; height: 100vh; width: min(690px, 100vw);
  background: #fff; z-index: 4001; display: flex; flex-direction: column;
  box-shadow: -24px 0 64px rgba(13, 15, 21, .18); text-align: left;
  --ink: #0d0f15; --body: #3d4250; --muted: #6b7280; --faint: #9aa2b1; --line: #ecedf1; --violet: #673de6;
}
.nv-backdrop { position: fixed; inset: 0; background: rgba(13, 15, 21, .4); z-index: 4000; }

.nv-head { display: flex; align-items: center; gap: 11px; padding: 18px 24px; border-bottom: 1px solid var(--line); }
.nv-head__ic { width: 34px; height: 34px; border-radius: 10px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; background: #f3efff; color: var(--violet); }
.nv-head__ic >>> svg { width: 19px; height: 19px; }
.nv-head__t { margin: 0; font-size: 17px; font-weight: 800; color: var(--ink); letter-spacing: -.02em; }
.nv-x { margin-left: auto; width: 34px; height: 34px; border-radius: 10px; border: 0; background: transparent; color: var(--muted); cursor: pointer; display: flex; align-items: center; justify-content: center; transition: background .15s; }
.nv-x svg { width: 18px; height: 18px; }
.nv-x:hover { background: #f3f4f7; color: var(--ink); }

.nv-tabs { display: flex; padding: 0 24px; border-bottom: 1px solid var(--line); }
.nv-tab { flex: 1; text-align: left; padding: 13px 0; border: 0; background: none; cursor: pointer; font-size: 13.5px; font-weight: 700; color: var(--faint); border-bottom: 2px solid transparent; margin-bottom: -1px; transition: color .15s; display: flex; align-items: center; gap: 7px; }
.nv-tab.on { color: var(--ink); border-bottom-color: var(--violet); }
.nv-b { background: #ffe8ec; color: #d1244a; font-size: 10.5px; font-weight: 800; min-width: 17px; height: 17px; line-height: 17px; border-radius: 9px; padding: 0 5px; text-align: center; }

.nv-body { flex: 1; overflow-y: auto; padding: 4px 20px 28px; background: #f7f8fa; }
.nv-body > .nv-c:first-child { margin-top: 18px; }
.nv-day { font-size: 10.5px; font-weight: 700; color: var(--faint); letter-spacing: .06em; text-transform: uppercase; padding: 18px 0 9px; }

/* Tarjeta compacta: sin borde, con la sombra del producto */
.nv-c { background: #fff; border-radius: 14px; padding: 14px 18px 12px; box-shadow: 0 4px 20px rgba(0, 0, 0, .05); margin-bottom: 10px; transition: box-shadow .18s cubic-bezier(.4, 0, .2, 1); animation: nvIn .34s cubic-bezier(.4, 0, .2, 1) both; }
.nv-c:hover { box-shadow: 0 6px 24px rgba(0, 0, 0, .08); }
.nv-c:nth-child(2) { animation-delay: .03s; }
.nv-c:nth-child(3) { animation-delay: .06s; }
.nv-c:nth-child(4) { animation-delay: .09s; }
@keyframes nvIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }

.nv-kind { display: inline-block; font-size: 9.5px; font-weight: 800; letter-spacing: .06em; text-transform: uppercase; padding: 2.5px 7px; border-radius: 5px; margin-bottom: 7px; }
.nv-kind--nov { background: #f0ecfe; color: #5a2fd6; }
.nv-kind--mej { background: #e7f0ff; color: #1a56d6; }
.nv-kind--cor { background: #e4f7ec; color: #087a3c; }

/* Marca de sin leer: publicado desde la última vez que miró */
.nv-new-dot { display: inline-flex; align-items: center; gap: 4px; margin-left: 6px; font-size: 9.5px; font-weight: 800; letter-spacing: .05em; text-transform: uppercase; color: #d1244a; }
.nv-new-dot::before { content: ""; width: 5px; height: 5px; border-radius: 50%; background: #f43f5e; }

.nv-c__t { margin: 0 0 4px; font-size: 14.5px; font-weight: 700; color: var(--ink); letter-spacing: -.015em; line-height: 1.3; }
.nv-c__b { margin: 0; font-size: 13px; line-height: 1.5; color: var(--body); }
.nv-c__b.is-clamp { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }

/* Pie: dos etiquetas pulsables, una a cada lado */
.nv-c__f { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-top: 11px; }
.nv-pill { display: inline-flex; align-items: center; gap: 5px; padding: 6px 11px; border: 0; border-radius: 8px; font-size: 12px; font-weight: 700; cursor: pointer; transition: background .15s, color .15s, transform .12s; }
.nv-pill svg { width: 13px; height: 13px; flex-shrink: 0; }
.nv-pill:active { transform: scale(.97); }
.nv-pill--more { background: #f2effe; color: #5a2fd6; }
.nv-pill--more:hover { background: #e8e2fd; }
.nv-pill--more svg { transition: transform .25s cubic-bezier(.4, 0, .2, 1); }
.nv-c.is-open .nv-pill--more svg { transform: rotate(180deg); }
.nv-pill--rep { background: #f6f7f9; color: #6b7280; }
.nv-pill--rep:hover { background: #fff3e6; color: #c2680b; }

/* Mosaico de capturas: el reparto cambia según cuántas haya, para que nunca
   sea una fila monótona. Cada pieza va sobre blanco y con sombra propia: así
   no hay bordes negros aunque la captura sea oscura. */
.nv-mos { display: grid; gap: 8px; margin-top: 12px; width: min(100%, 380px); }
.nv-mos--1 { grid-template-columns: minmax(0, 68%); grid-auto-rows: 124px; }
.nv-mos--2 { grid-template-columns: 1fr 1fr; grid-auto-rows: 108px; }
/* Tres: dos apiladas a la izquierda y una grande al lado */
.nv-mos--3 { grid-template-columns: 1fr 1.42fr; grid-template-rows: 70px 70px; }
.nv-mos--3 .nv-tile:nth-child(3) { grid-column: 2; grid-row: 1 / span 2; }
.nv-mos--4 { grid-template-columns: 1fr 1fr; grid-template-rows: 90px 90px; }
/* Cinco o más: cuatro en cuadrícula y una grande a su derecha */
.nv-mos--5 { grid-template-columns: 1fr 1fr 1.55fr; grid-template-rows: 74px 74px; }
.nv-mos--5 .nv-tile:nth-child(5) { grid-column: 3; grid-row: 1 / span 2; }

.nv-tile {
  position: relative; padding: 0; border: 0; overflow: hidden; cursor: zoom-in;
  border-radius: 11px; background: #fff; min-width: 0;
  box-shadow: 0 2px 8px rgba(13, 15, 21, .09), 0 0 0 1px rgba(13, 15, 21, .045);
  transition: transform .22s cubic-bezier(.23, 1, .32, 1), box-shadow .22s cubic-bezier(.23, 1, .32, 1);
}
.nv-tile:hover { transform: translateY(-2px); box-shadow: 0 10px 24px rgba(13, 15, 21, .16), 0 0 0 1px rgba(103, 61, 230, .25); z-index: 1; }
.nv-tile:active { transform: translateY(-1px) scale(.985); }
.nv-tile img { width: 100%; height: 100%; object-fit: cover; display: block; }
/* La lupa aparece al pasar por encima: dice "esto se puede abrir" */
.nv-tile__lupa {
  position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
  background: rgba(13, 15, 21, .34); color: #fff; opacity: 0;
  transition: opacity .18s ease;
}
.nv-tile__lupa svg { width: 20px; height: 20px; transform: scale(.85); transition: transform .22s cubic-bezier(.23, 1, .32, 1); }
.nv-tile:hover .nv-tile__lupa { opacity: 1; }
.nv-tile:hover .nv-tile__lupa svg { transform: scale(1); }
.nv-tile__mas {
  position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
  background: rgba(13, 15, 21, .55); color: #fff; font-size: 17px; font-weight: 800; letter-spacing: -.02em;
}
.nv-tile:hover .nv-tile__mas { background: rgba(13, 15, 21, .66); }

/* Reportes: el estado lo lleva un punto, no una franja lateral */
.nv-t { background: #fff; border-radius: 14px; padding: 15px 18px; box-shadow: 0 4px 20px rgba(0, 0, 0, .05); margin-bottom: 11px; }
/* La primera tarjeta de Reportes quedaba pegada al borde: en Novedades ese aire
   lo daba el rótulo del día, que aquí no existe. */
.nv-t:first-child { margin-top: 18px; }
.nv-t__hd { display: flex; align-items: center; gap: 7px; margin-bottom: 6px; }
.nv-t__st { display: inline-flex; align-items: center; gap: 6px; font-size: 11.5px; font-weight: 800; }
.nv-t__st::before { content: ""; width: 7px; height: 7px; border-radius: 50%; background: currentColor; }
.nv-t--o .nv-t__st { color: #4f46e5; }
.nv-t--p .nv-t__st { color: #c2680b; }
.nv-t--d .nv-t__st { color: #15803d; }
.nv-t--c .nv-t__st { color: var(--faint); }
.nv-t__id { margin-left: auto; font-size: 11px; color: var(--faint); }
.nv-t__t { margin: 0 0 4px; font-size: 14.5px; font-weight: 700; color: var(--ink); letter-spacing: -.015em; }
.nv-t__d { margin: 0; font-size: 13px; line-height: 1.6; color: var(--body); }
.nv-t__f { display: flex; gap: 12px; margin-top: 9px; font-size: 11.5px; color: var(--faint); }
.nv-new { width: 100%; margin-top: 20px; padding: 12px; border: 1px dashed #d5d9e1; border-radius: 12px; background: none; color: var(--muted); font-size: 13px; font-weight: 700; cursor: pointer; transition: all .16s; }
.nv-new:hover { border-color: var(--violet); color: var(--violet); background: #faf8ff; }

.nv-empty { text-align: center; color: var(--faint); padding: 60px 20px; }
.nv-empty >>> svg { width: 28px; height: 28px; margin-bottom: 10px; }

/* ── Conversación del reporte ─────────────────────────────────────────────*/
.nv-chat { flex: 1; display: flex; flex-direction: column; min-height: 0; padding: 16px 20px 14px; background: #f7f8fa; }
.nv-chat__hd { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
.nv-chat__cab { padding-bottom: 12px; margin-bottom: 4px; border-bottom: 1px solid var(--line); }
.nv-chat__t { margin: 0 0 2px; font-size: 15.5px; font-weight: 800; color: var(--ink); letter-spacing: -.02em; line-height: 1.3; }
.nv-chat__sub { font-size: 11.5px; color: var(--faint); }
.nv-st--o { color: #4f46e5; } .nv-st--p { color: #c2680b; } .nv-st--d { color: #15803d; } .nv-st--c { color: var(--faint); }

.nv-hilo { flex: 1; overflow-y: auto; padding: 14px 2px 4px; scroll-behavior: smooth; }
.nv-hilo__in { display: flex; flex-direction: column; }
.nv-hilo__ini { display: block; text-align: center; font-size: 10.5px; font-weight: 700; letter-spacing: .05em; text-transform: uppercase; color: var(--faint); margin-bottom: 14px; }
.nv-hilo__espera { margin: 16px auto 0; max-width: 78%; text-align: center; font-size: 12px; line-height: 1.55; color: var(--faint); }

/* Fila = avatar + burbuja. El avatar solo en la primera de cada tanda. */
.nv-fila { display: flex; align-items: flex-end; gap: 8px; margin-top: 10px; }
.nv-fila.is-seguido { margin-top: 3px; }
.nv-fila--yo { justify-content: flex-end; }
.nv-fila--eq { justify-content: flex-start; }
.nv-av { width: 26px; height: 26px; border-radius: 9px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; background: #ece6fd; color: var(--violet); }
.nv-av svg { width: 15px; height: 15px; }
.nv-av.is-hueco { visibility: hidden; }

.nv-msg { max-width: 80%; padding: 9px 13px; border-radius: 15px; box-shadow: 0 1px 3px rgba(13, 15, 21, .06); }
.nv-msg p { margin: 0; font-size: 13.5px; line-height: 1.5; white-space: pre-line; word-break: break-word; }
.nv-msg__m { display: block; margin-top: 4px; font-size: 10.5px; opacity: .68; }
.nv-msg--host { background: var(--violet); color: #fff; border-bottom-right-radius: 5px; }
.nv-msg--agent { background: #fff; color: var(--ink); border-bottom-left-radius: 5px; }
.nv-fila--yo.is-seguido .nv-msg { border-top-right-radius: 6px; }
.nv-fila--eq.is-seguido .nv-msg { border-top-left-radius: 6px; }
.nv-msg--typing { display: flex; gap: 4px; padding: 13px 15px; }
.nv-dot { width: 6px; height: 6px; border-radius: 50%; background: #b4bac6; animation: nvT 1.2s infinite; }
.nv-dot:nth-child(2) { animation-delay: .18s; }
.nv-dot:nth-child(3) { animation-delay: .36s; }
@keyframes nvT { 0%,60%,100% { opacity: .3; transform: translateY(0); } 30% { opacity: 1; transform: translateY(-3px); } }

/* Las burbujas entran desde abajo, nunca desde la nada */
.nv-burbuja-enter-active { transition: opacity .26s cubic-bezier(.23, 1, .32, 1), transform .26s cubic-bezier(.23, 1, .32, 1); }
.nv-burbuja-leave-active { transition: opacity .16s ease; position: absolute; }
.nv-burbuja-enter { opacity: 0; transform: translateY(10px) scale(.97); }
.nv-burbuja-leave-to { opacity: 0; }
.nv-burbuja-move { transition: transform .26s cubic-bezier(.23, 1, .32, 1); }

/* Caja de respuesta: clases propias, sin heredar de .nv-i/.nv-send (el ancho
   del 100% de esas dos aplastaba la caja hasta hacerla invisible). */
.nv-rp {
  display: flex; align-items: flex-end; gap: 8px; margin-top: 12px; padding: 6px 6px 6px 14px;
  background: #fff; border: 1.5px solid #e3e6eb; border-radius: 20px;
  transition: border-color .16s ease, box-shadow .16s ease;
}
.nv-rp.is-foco { border-color: var(--violet); box-shadow: 0 0 0 3px rgba(103, 61, 230, .12); }
.nv-rp__ta {
  flex: 1 1 auto; min-width: 0; height: 38px; max-height: 132px; padding: 9px 0; margin: 0;
  border: 0; outline: none; resize: none; background: transparent; overflow-y: auto;
  font-size: 13.5px; line-height: 1.45; color: var(--ink);
}
.nv-rp__ta::placeholder { color: #a8aeba; }
.nv-rp__go {
  flex: 0 0 auto; width: 38px; height: 38px; border: 0; border-radius: 50%; cursor: pointer;
  background: var(--violet); color: #fff; display: flex; align-items: center; justify-content: center;
  transition: background .16s ease, transform .16s cubic-bezier(.23, 1, .32, 1), opacity .16s ease;
}
.nv-rp__go svg { width: 17px; height: 17px; margin-left: -1px; }
.nv-rp__go:hover:not(:disabled) { background: #5a2fd6; }
.nv-rp__go:active:not(:disabled) { transform: scale(.92); }
.nv-rp__go:disabled { background: #dfe2e8; color: #fff; cursor: default; }
.nv-spin { width: 14px; height: 14px; border: 2px solid rgba(255, 255, 255, .45); border-top-color: #fff; border-radius: 50%; animation: nvSpin .7s linear infinite; }
@keyframes nvSpin { to { transform: rotate(360deg); } }
.nv-rp__pista { display: block; margin-top: 7px; text-align: center; font-size: 10.5px; color: var(--faint); }
.nv-rp__pista b { font-weight: 700; color: var(--muted); }

.nv-t { cursor: pointer; transition: box-shadow .16s ease; }
.nv-t:hover { box-shadow: 0 6px 22px rgba(13,15,21,.09); }
.nv-t__go { margin-left: auto; color: var(--violet); font-weight: 700; }
.nv-t__unread { background: #f43f5e; color: #fff; font-size: 10px; font-weight: 800; min-width: 16px; height: 16px; line-height: 16px; border-radius: 8px; padding: 0 5px; text-align: center; }

.nv-form { padding: 20px 24px 28px; overflow-y: auto; }
.nv-back { display: inline-flex; align-items: center; gap: 5px; border: 0; background: none; color: var(--muted); font-size: 13px; font-weight: 600; cursor: pointer; padding: 0; margin-bottom: 18px; }
.nv-back svg { width: 15px; height: 15px; }
.nv-form__t { margin: 0 0 4px; font-size: 18px; font-weight: 800; color: var(--ink); letter-spacing: -.02em; }
.nv-form__s { margin: 0 0 18px; font-size: 13px; color: var(--muted); line-height: 1.55; }
.nv-ctx { display: flex; align-items: baseline; gap: 7px; padding: 11px 13px; border-radius: 10px; background: #f7f5ff; font-size: 12.5px; color: #4a2bb8; font-weight: 600; margin-bottom: 18px; }
.nv-ctx__l { font-size: 10px; text-transform: uppercase; letter-spacing: .05em; color: #8b7cc4; font-weight: 800; }
.nv-err { background: #fef2f2; color: #dc2626; font-size: 12.5px; font-weight: 600; padding: 10px 13px; border-radius: 10px; margin-bottom: 16px; }
.nv-l { display: block; font-size: 12.5px; font-weight: 700; color: var(--ink); margin: 0 0 6px; }
.nv-opt { font-weight: 500; color: var(--faint); }
.nv-i { width: 100%; padding: 11px 13px; margin-bottom: 16px; border: 1.5px solid #e3e6eb; border-radius: 11px; font-size: 14px; color: var(--ink); background: #fff; outline: none; transition: border-color .14s, box-shadow .14s; }
.nv-i::placeholder { color: #a8aeba; }
.nv-i:focus { border-color: var(--violet); box-shadow: 0 0 0 3px rgba(103, 61, 230, .12); }
.nv-i--a { resize: vertical; min-height: 100px; line-height: 1.55; }
.nv-shots { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 10px; }
.nv-shot { position: relative; width: 76px; height: 76px; border-radius: 10px; overflow: hidden; }
.nv-shot img { width: 100%; height: 100%; object-fit: cover; }
.nv-shot__x { position: absolute; top: 2px; right: 2px; width: 20px; height: 20px; border: 0; border-radius: 6px; background: rgba(13, 15, 21, .7); color: #fff; cursor: pointer; font-size: 14px; line-height: 1; }
.nv-drop { display: flex; flex-direction: column; align-items: center; gap: 7px; padding: 20px; border: 1.5px dashed #d5d9e1; border-radius: 12px; color: var(--faint); font-size: 12.5px; cursor: pointer; transition: all .16s; margin-bottom: 20px; }
.nv-drop svg { width: 21px; height: 21px; }
.nv-drop:hover { border-color: var(--violet); color: var(--violet); background: #faf8ff; }
.nv-send { width: 100%; padding: 13px; border: 0; border-radius: 12px; background: var(--violet); color: #fff; font-size: 14px; font-weight: 700; cursor: pointer; transition: background .15s, transform .15s; }
.nv-send:hover { background: #5a2fd6; }
.nv-send:disabled { opacity: .6; cursor: default; }
.nv-send:active { transform: scale(.99); }

/* ── Visor con carrusel ───────────────────────────────────────────────────*/
/* Fondo velado y desenfocado en vez de negro sólido: la captura queda sobre
   una tarjeta blanca, así ninguna imagen acaba rodeada de negro. */
.nv-visor {
  position: fixed; inset: 0; z-index: 4100; display: flex; align-items: center; justify-content: center;
  gap: 10px; padding: 28px 20px; cursor: zoom-out;
  background: rgba(18, 20, 28, .72); backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px);
}
.nv-visor__centro { display: flex; flex-direction: column; align-items: center; gap: 12px; min-width: 0; max-width: min(100%, 1000px); cursor: default; }
/* El marco NO amplía por encima del tamaño real: estirar una captura de 1800px
   a pantalla completa es lo que la hacía verse borrosa. */
.nv-visor__marco {
  margin: 0; padding: 8px; background: #fff; border-radius: 16px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, .38); max-width: 100%;
}
.nv-visor__marco img { display: block; max-width: 100%; max-height: min(70vh, calc(100vh - 215px)); width: auto; height: auto; border-radius: 9px; }

.nv-visor__x, .nv-visor__nav {
  flex-shrink: 0; border: 0; cursor: pointer; color: #fff; display: flex; align-items: center; justify-content: center;
  background: rgba(255, 255, 255, .14); backdrop-filter: blur(6px);
  transition: background .16s ease, transform .16s cubic-bezier(.23, 1, .32, 1);
}
.nv-visor__x { position: absolute; top: 20px; right: 22px; width: 38px; height: 38px; border-radius: 12px; }
.nv-visor__x svg { width: 19px; height: 19px; }
.nv-visor__nav { width: 44px; height: 44px; border-radius: 50%; }
.nv-visor__nav svg { width: 21px; height: 21px; }
.nv-visor__x:hover, .nv-visor__nav:hover { background: rgba(255, 255, 255, .26); }
.nv-visor__nav:active { transform: scale(.92); }

.nv-visor__tira { display: flex; gap: 7px; padding: 6px; border-radius: 13px; background: rgba(255, 255, 255, .1); max-width: 100%; overflow-x: auto; }
.nv-mini { width: 52px; height: 40px; flex-shrink: 0; padding: 0; border: 0; border-radius: 8px; overflow: hidden; cursor: pointer; opacity: .5; background: #fff; transition: opacity .16s ease, box-shadow .16s ease, transform .16s cubic-bezier(.23, 1, .32, 1); }
.nv-mini img { width: 100%; height: 100%; object-fit: cover; display: block; }
.nv-mini:hover { opacity: .85; }
.nv-mini.on { opacity: 1; box-shadow: 0 0 0 2px #fff; transform: translateY(-2px); }
.nv-visor__c { position: absolute; bottom: 22px; left: 50%; transform: translateX(-50%); font-size: 12px; font-weight: 700; color: rgba(255, 255, 255, .8); }

/* Cambio de imagen: entra por el lado hacia el que vas */
.nv-sw-sig-enter-active, .nv-sw-ant-enter-active { transition: opacity .2s ease, transform .28s cubic-bezier(.23, 1, .32, 1); }
.nv-sw-sig-leave-active, .nv-sw-ant-leave-active { transition: opacity .14s ease, transform .14s ease; }
.nv-sw-sig-enter { opacity: 0; transform: translateX(26px); }
.nv-sw-sig-leave-to { opacity: 0; transform: translateX(-16px); }
.nv-sw-ant-enter { opacity: 0; transform: translateX(-26px); }
.nv-sw-ant-leave-to { opacity: 0; transform: translateX(16px); }

.nv-fade-enter-active, .nv-fade-leave-active { transition: opacity .2s ease; }
.nv-fade-enter, .nv-fade-leave-to { opacity: 0; }
.nv-slide-enter-active, .nv-slide-leave-active { transition: transform .34s cubic-bezier(.32, .72, 0, 1); }
.nv-slide-enter, .nv-slide-leave-to { transform: translateX(100%); }
@media (prefers-reduced-motion: reduce) {
  .nv-slide-enter-active, .nv-slide-leave-active,
  .nv-fade-enter-active, .nv-fade-leave-active,
  .nv-burbuja-enter-active, .nv-burbuja-leave-active, .nv-burbuja-move,
  .nv-sw-sig-enter-active, .nv-sw-ant-enter-active,
  .nv-sw-sig-leave-active, .nv-sw-ant-leave-active,
  .nv-pill svg, .nv-tile, .nv-tile__lupa, .nv-mini, .nv-rp__go { transition: none; }
  .nv-c { animation: none; }
  .nv-burbuja-enter, .nv-sw-sig-enter, .nv-sw-ant-enter { transform: none; }
  .nv-hilo { scroll-behavior: auto; }
  .nv-dot { animation: none; opacity: .6; }
}
</style>
